import {useMultiStyleConfig} from '@chakra-ui/react';
import type {ThemingProps, StyleFunctionProps} from '@chakra-ui/styled-system';
import {defineCssVars, defineStyleConfig} from '@chakra-ui/styled-system';
import {mode} from '@chakra-ui/theme-tools';
import {createAnatomy} from '@zag-js/anatomy';

import {tokens as dark} from '../../generated/dark';
import {tokens as light} from '../../generated/light';

export const vars = defineCssVars(`text`, [`font-size`, `line-height`, `font-weight`]);

export const textAnatomy = createAnatomy(`text`, [`root`]).build();

const truncate = {
  textOverflow: `ellipsis`,
  whiteSpace: `nowrap`,
  overflow: `hidden`,
};

export const textTheme = defineStyleConfig({
  baseStyle: ({isTruncated}: StyleFunctionProps & {isTruncated?: boolean} = {} as any) => {
    return {
      fontFamily: `inter`,
      [vars[`font-weight`].variable]: `fontWeights.normal`,
      lineHeight: vars[`line-height`].reference,
      fontSize: vars[`font-size`].reference,
      ...(isTruncated && truncate),
    };
  },
  sizes: {
    sm: {
      [vars[`font-weight`].variable]: `fontWeights.text.sm`,
      [vars[`font-size`].variable]: `fontSizes.text.sm`,
      [vars[`line-height`].variable]: `lineHeights.text.sm`,
    },
    md: {
      [vars[`font-weight`].variable]: `fontWeights.text.md`,
      [vars[`font-size`].variable]: `fontSizes.text.md`,
      [vars[`line-height`].variable]: `lineHeights.text.md`,
    },
    lg: {
      [vars[`font-weight`].variable]: `fontWeights.text.lg`,
      [vars[`font-size`].variable]: `fontSizes.text.lg`,
      [vars[`line-height`].variable]: `lineHeights.text.lg`,
    },
  },
  variants: {
    primary: props => {
      const color = mode(light.semantic.colors.text.primary, dark.semantic.colors.text.primary)(props);
      return {
        color,
      };
    },
    secondary: props => {
      const color = mode(light.semantic.colors.text.secondary, dark.semantic.colors.text.secondary)(props);
      return {
        color,
      };
    },
    tertiary: props => {
      const color = mode(light.semantic.colors.text.tertiary, dark.semantic.colors.text.tertiary)(props);
      return {
        color,
      };
    },
  },
  defaultProps: {
    size: `md`,
  },
});

export const useTextStyleConfig = (props: ThemingProps) => useMultiStyleConfig(`Text`, props);
